import React, { memo, useEffect } from 'react'
import clsx from 'clsx'
import { Controller } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid2'
import { whatsappEasyAgencias } from 'vars'
import EventEmitter from 'services/EventEmitter'
import NumberFormat from 'react-number-format'
import { LEAD_TYPE } from 'enums'
import { useLead } from 'hooks/useLead'
import { useModal } from 'components/shared/Modal'
import dynamic from 'next/dynamic'
import { setValue } from 'redux/modules/appraisal'
import usePersonalDataForm from './usePersonalDataForm'
import sx from './styles'

const AcutionRegister = ({
  // onSubmit: onSubmitProp,
  register = {},
}) => {
  const { formatMessage: f } = useIntl()
  const { personalData } = useSelector((s) => s.checkout)
  const {
    formState,
    control,
    contactName,
    email,
    phone,
    name,
    postalCode,
  } = usePersonalDataForm(register)

  const dispatch = useDispatch()
  const ErrorModalDynamic = dynamic(() => import('components/shared/ErrorModal'))
  const FeedbackAuctionModalDynamic = dynamic(() => import('components/Auction/FeedbackAuctionModal'))
  const { handleOpenModal, handleCloseModal } = useModal()

  const leadMutation = useLead(LEAD_TYPE.DEALERS_ONBOARDING)

  const whatsappMessageFinal = 'Hola, estoy interesado en ClikAuto Subastas. ¿Me puedes dar más información?'

  const congratulationModalFeedback = () => {
    handleOpenModal({
      title: 'AUCTIONS_FORM_FEEDBACK_TITLE',
      body: (
        <FeedbackAuctionModalDynamic
          onRedirect={handleCloseModal}
          handleCloseModal={handleCloseModal}
        />
      ),
      configProps: {
        showDividers: true,
        isFormattedTitle: true,
        titleAlign: 'left',
        showClose: true,
        sxProps: sx.modalContainer,
        scrollLock: true,
      },
    })
  }

  const handleErrorModal = () => {
    handleOpenModal({
      title: 'ERROR_MODAL_TITLE',
      body: (
        <ErrorModalDynamic
          onCloseModal={handleCloseModal}
        />
      ),
      configProps: {
        sxProp: { title: sx.modalTitle },
        isFormattedTitle: true,
      },
    })
  }

  const handleSubmit = () => {
    const bodyLead = {
      phone,
      contactName,
      email,
      name,
      postalCode,
    }
    leadMutation.mutate(bodyLead, {
      onSuccess: (response) => {
        dispatch(
          setValue({
            key: 'leadId',
            value: response.data?.id,
            persist: false,
          }),
          congratulationModalFeedback(),
        )
      },
      onError: (err) => {
        // eslint-disable-next-line no-console
        console.log('error en el mutate', err)
        handleErrorModal()
      },
    })
  }

  useEffect(() => {
    if (personalData > 0) {
      handleSubmit(true)
    }
  }, [personalData])

  return (
    <>
      <Box sx={sx.root}>
        <Box>
          <Box sx={{ ...sx.contentController, ...sx.inputGroup }}>
            <Controller
              name="contactName"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  sx={sx.field}
                  placeholder={f({
                    id: 'AUCTIONS_REGISTER_FIELD_NAME',
                  })}
                  label={`${f({
                    id: 'AUCTIONS_REGISTER_FIELD_NAME',
                  })}`}
                  value={field.value}
                  required
                  onChange={(e) => {
                    field.onChange(e)
                  }}
                  fullWidth
                  error={fieldState.invalid}
                  className={clsx('Mui-style', { touched: contactName })}
                />
              )}
            />
          </Box>

          <Box sx={{ ...sx.contentController, ...sx.inputGroup }}>
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  sx={sx.field}
                  placeholder={f({
                    id: 'AUCTIONS_REGISTER_FIELD_EMAIL',
                  })}
                  label={`${f({
                    id: 'AUCTIONS_REGISTER_FIELD_EMAIL',
                  })}`}
                  value={field.value}
                  required
                  onChange={(e) => {
                    field.onChange(e)
                  }}
                  fullWidth
                  error={fieldState.invalid}
                  className={clsx('Mui-style', { touched: email })}
                />
              )}
            />
          </Box>

          <Box sx={{ ...sx.contentController, ...sx.inputGroup }}>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <NumberFormat
                  {...field}
                  customInput={TextField}
                  sx={sx.field}
                  value={field.value}
                  placeholder={f({ id: 'AUCTIONS_REGISTER_PHONE' })}
                  label={`${f({
                    id: 'AUCTIONS_REGISTER_PHONE',
                  })}`}
                  required
                  onChange={field.onChange}
                  allowNegative={false}
                  isNumericString
                  isAllowed={({ value }) => value.length <= 10}
                  fullWidth
                  className={clsx('Mui-style', { touched: phone })}
                  type="tel"
                  prefix=""
                />
              )}
            />
          </Box>

          <Box sx={{ ...sx.contentController, ...sx.inputGroup }}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  sx={sx.field}
                  placeholder={f({
                    id: 'AUCTIONS_REGISTER_AGENCY',
                  })}
                  label={`${f({
                    id: 'AUCTIONS_REGISTER_AGENCY',
                  })}`}
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e)
                  }}
                  fullWidth
                  required
                  error={fieldState.invalid}
                  className={clsx('Mui-style', { touched: name })}
                />
              )}
            />
          </Box>

          <Box sx={{ ...sx.contentController, ...sx.inputGroup }}>
            <Controller
              name="postalCode"
              control={control}
              render={({ field }) => (
                <NumberFormat
                  {...field}
                  customInput={TextField}
                  sx={sx.field}
                  value={field.value}
                  placeholder={f({ id: 'AUCTIONS_REGISTER_POSTAL' })}
                  label={`${f({
                    id: 'AUCTIONS_REGISTER_POSTAL',
                  })}`}
                  required
                  onChange={field.onChange}
                  allowNegative={false}
                  isNumericString
                  isAllowed={({ value }) => value.length <= 5}
                  fullWidth
                  className={clsx('Mui-style', { touched: postalCode })}
                  type="tel"
                  prefix=""
                />
              )}
            />
          </Box>
        </Box>

        <Box
          sx={sx.finishLaterContent}
        >
          <Button
            fullWidth
            color="primary"
            variant="special"
            sx={sx.btn}
            disabled={(!formState.isValid)}
            onClick={() => { handleSubmit() }}
          >
            <FormattedMessage id="AUCTIONS_REGISTER_MAIN_CTA" />
          </Button>
        </Box>

        <Grid
          container
          sx={sx.linksContainers}
        >

          <Grid
            size={12}
            sx={sx.linklogin}
          >
            <Button
              variant="text"
              onClick={() => EventEmitter.emit('open-login')}
            >
              <Typography
                variant="link-text"
              >
                <FormattedMessage id="AUCTIONS_REGISTER_SWITCH_LOGIN" />
              </Typography>
            </Button>
          </Grid>

          <Grid
            size={12}
            sx={sx.linklogin}
          >
            <Button
              variant="text"
              onClick={() => EventEmitter.emit('open-register')}
            >
              <Typography
                component="a"
                variant="link-text"
                target="_blank"
                rel="noopener noreferrer"
                href={`//wa.me/${whatsappEasyAgencias}?text=${whatsappMessageFinal}`}
              >
                <FormattedMessage id="AUCTIONS_REGISTER_CONTACT_LOGIN" />
              </Typography>
            </Button>
          </Grid>

          <Grid
            size={12}
            sx={sx.linklogin}
          >
            <Button
              variant="text"
            >
              <Typography
                component="a"
                variant="link-text"
                rel="noopener noreferrer"
                href="/terminos-subastas"
              >
                <FormattedMessage id="TERMS_AND_CONDITIONS_TITLE" />
              </Typography>
            </Button>
          </Grid>

        </Grid>
      </Box>
    </>
  )
}

export default memo(AcutionRegister)
